'use client'

import { useMemo, useState } from 'react'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import { birdiePastRoundsCopy } from 'src/components/BirdieRoundCard/birdieCopy'
import type { Marble } from 'src/types/domain/Marble'
import type { Race } from 'src/types/domain/Race'
import './birdie-board.scss'
import { BirdieRoundDisc } from './BirdieRoundCard'
import { BirdieRoundSheet } from './BirdieRoundSheet'
import { BirdieBallDefs } from './GolfBall'
import { birdiePastRounds, type BirdiePastRound } from './pastRounds'
import { buildRoundCardViews } from './roundCardModel'
import { useBirdieRevealClock } from './useBirdieRevealClock'
import { useBirdieTranslate } from './useBirdieTranslate'

type Props = Readonly<{
  races: readonly Race[]
  marbles: readonly Marble[]
}>

/** The disc's reference diameter, px (the mockup's 2.66 cqw at 12.8 px/cqw). */
// 40 px, a hair under the rail's 42-px buttons (owner, 2026-09-08: the outcome
// balls at 34 px were too small to read beside the make chance).
const DISC_SIZE_PX = 40

/**
 * How many discs the rail holds — the slot's own count, not `pastRounds`' cap.
 *
 * SIX, where the crash rail shows eight pills, because a 34-px disc is not a
 * 24-px pill. Measured on the compiled stylesheet: six discs put the whole
 * `.right-floating-panel` at 381 px tall against the crash rail's 386, so the
 * two rails hang the same amount off the frame's half-height; eight put it at
 * 459 and started crowding a short window. It is also the count B19's geometry
 * resolved for the reference frame, for the same reason.
 */
const BIRDIE_RAIL_ROUNDS = 6

/**
 * FOUR on a phone. The rail's slot is shorter there and six discs overflow their container
 * (owner 2026-09-16). The desktop count is unchanged — it was measured against the crash rail's
 * height and still holds.
 */
const BIRDIE_RAIL_ROUNDS_MOBILE = 2

/**
 * Birdie's past rounds — the Birdie RENDERING of the shared results rail.
 *
 * B19 drew this column itself, in a box `boardGeometry` cut out of the icon
 * rail. B19b deletes that box: the owner's rule is one surface per job ("if it's
 * this golf birdie game it will show like the design you've made else like it's
 * shown for the other games so we don't reinvent it all"), so the discs are now
 * what `<ShowRaceResults>` renders in its results slot for a Birdie game, in the
 * same rail, with the same "Past Games" button and the same phone row, where
 * every other game's crash pills and marble winners already live.
 *
 * It is a CHILD of that branch rather than inline JSX so its three hooks — the
 * reveal clock, the translator and the modal's own state — run for a Birdie game
 * ONLY. `<ShowRaceResults>` itself gains no hook, and the eleven other games'
 * render path is unchanged.
 *
 * Owner, 2026-09-08: "showing the past results does not make much sense as it
 * changes each time with the probability so a hard past bet could be a easy bet
 * on the next round … players would expect to see some sort of past game
 * stats". A Birdie disc is therefore the two things that DO compare across
 * rounds that are repriced every round: how likely the golfer was to hole one
 * (the make chance, on top) and what actually happened (the three putts, as real
 * balls under it). What it PAID is not on the disc — it needs the golfer beside
 * it to mean anything, and that is what the modal is for.
 *
 * Clicking a disc opens the round card as a fairness modal, the same
 * `<BirdieRoundSheet>` a sidebar row opens. The interaction shape is the shared
 * rail's own (`ShowRaceResults` holds a selected round id and resolves it back
 * against the list), so a round that scrolls out of history closes its own modal
 * rather than freezing a stale one open.
 *
 * This rail reads the STORE's window only — the ~10 rounds the socket keeps
 * current — because it shows at most six. The deep read belongs to the
 * surfaces that need 50 rounds (the history sidebar and the stats panel), and
 * both of those take it themselves through `useDeepRaceHistory`.
 *
 * The whole component renders nothing until at least one round is showable — a
 * rail of empty discs on a fresh game would be chrome advertising an absence.
 */
export function BirdiePastRounds({ races, marbles }: Props) {
  const { t, translate, formatPercent, formatInteger } = useBirdieTranslate()
  const copy = useMemo(() => birdiePastRoundsCopy(translate), [translate])

  // ANTI-SPOILER CLOCK — a round is withheld until its own reveal hold has
  // elapsed, exactly as the capsule withholds the final score.
  const nowMs = useBirdieRevealClock(races)

  const { isMobile } = useDeviceInfo()
  const rounds = useMemo(
    () =>
      birdiePastRounds({
        races,
        marbles,
        nowMs,
        limit: isMobile ? BIRDIE_RAIL_ROUNDS_MOBILE : BIRDIE_RAIL_ROUNDS
      }),
    [isMobile, marbles, nowMs, races]
  )

  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null)
  // Read when the modal OPENS, not on every render: "2m ago" must not tick
  // while a player is reading the reveal, and a value read in render would make
  // the card impure.
  const [openedAtMs, setOpenedAtMs] = useState<number>(() => Date.now())

  const views = useMemo(
    () =>
      buildRoundCardViews({
        rounds,
        t: translate,
        formatPercent,
        formatInteger,
        nowMs: openedAtMs
      }),
    [formatInteger, formatPercent, openedAtMs, rounds, translate]
  )

  const selected: BirdiePastRound | undefined = rounds.find(
    (round) => round.roundId === selectedRoundId
  )
  const selectedView = selectedRoundId === null ? undefined : views.get(selectedRoundId)

  if (rounds.length === 0) return null

  return (
    <div className="birdie-past">
      {/* The rail sits in `.right-floating-panel` / `.bottom-floating-panel`,
          outside the board's own overlay, so the balls' gradients have to be
          declared again here or every glyph draws unpainted. The ids are fixed
          and the definitions identical, which is what makes them idempotent
          (`GolfBall.tsx`). */}
      <BirdieBallDefs />
      {/* `role="list"` explicitly: Safari drops the implicit list role from a
          `<ul>` the moment `list-style: none` is applied, which is exactly what
          this rail does. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- NOT redundant in
          Safari: it drops a <ul>'s implicit list role the moment `list-style:
          none` is applied, which is exactly what this rail does. */}
      <ul aria-label={copy.title} className="birdie-past__list" role="list">
        {rounds.map((round) => {
          const view = views.get(round.roundId)
          if (view === undefined) return null
          return (
            <li className="birdie-past__item" key={round.roundId}>
              <BirdieRoundDisc
                onOpen={() => {
                  setOpenedAtMs(Date.now())
                  setSelectedRoundId(round.roundId)
                }}
                size={DISC_SIZE_PX}
                view={view}
              />
            </li>
          )
        })}
      </ul>
      {selected === undefined || selectedView === undefined ? null : (
        <BirdieRoundSheet
          copy={copy}
          onClose={() => {
            setSelectedRoundId(null)
          }}
          round={selected}
          t={t}
          view={selectedView}
        />
      )}
    </div>
  )
}
